import { computed, inject, Injectable, Signal } from '@angular/core';
import {
  IHttpStatus,
  IScanDocumentData,
  IScanDocumentModel,
  IScanMethodData,
  IUploadDocumentType,
  TLastScanActions
} from '@partners/api';
import { IOcrScanDocsOption } from '@partners/catalog';
import { filterStatus, HttpService, tapResult } from '@partners/http';
import { ScanDocumentStore } from '@partners/state';
import { blobToRawBase64, ImageLoadError, resizeToBlob, takeOnce, TImageFileType } from '@partners/utils';
import { catchError, lastValueFrom, Observable, tap } from 'rxjs';
import { NavigationAdapter } from '../../../navigation';
import { UploadDocumentSeverities } from '../errors/upload-document.errors';

@Injectable({ providedIn: 'root' })
export class UploadDocumentFacade {
  readonly documents$$: Signal<IUploadDocumentType[]>;
  #httpService = inject(HttpService);
  #scanDocumentStore = inject(ScanDocumentStore);
  #navAdapter = inject(NavigationAdapter);
  #uploadDocumentSeverities = inject(UploadDocumentSeverities);
  readonly data$$: Signal<IScanDocumentData>;
  readonly model$$: Signal<IScanDocumentModel>;
  readonly selectedDocToScan$$: Signal<number>;
  readonly lastScanAction$$: Signal<TLastScanActions>;

  constructor() {
    this.selectedDocToScan$$ = computed(() => this.#scanDocumentStore.model$$()?.selectedDocId);
    this.lastScanAction$$ = computed(() => this.#scanDocumentStore.lastScanAction$$());

    this.documents$$ = computed<IUploadDocumentType[]>(() => this.#scanDocumentStore.data$$()?.documents ?? []);
    this.data$$ = computed(() => this.#scanDocumentStore.data$$());
    this.model$$ = computed(() => this.#scanDocumentStore.model$$());
  }

  updateDocNameForDisplay(documentToScanOptions: IOcrScanDocsOption[]): IOcrScanDocsOption[] {
    return documentToScanOptions?.map(({ key, value }) => {
      return { key, value };
    });
  }

  /**
   * @description Create the upload document model from the file and upload document type.
   * @param file The file to upload.
   * @param uploadDocumentType The type of document being uploaded.
   * @returns A promise that resolves to the upload document model.
   */
  async #createUploadDocumentModel(file: File, uploadDocumentType: string): Promise<IScanDocumentModel> {
    //resize the image before converting to base64 to reduced size
    const resizedToBlob = await resizeToBlob(file);
    //convert the blob to base64
    const documentImage = await blobToRawBase64(resizedToBlob);
    //create the upload document model
    const uploadDocument = { documentImage, uploadDocumentType };

    return uploadDocument;
  }

  /**
   *
   * @description
   *  Uploads a document image to the server.
   *  - extracts the selected document type from the store
   * - creates the upload document model
   *  - sends the upload request to the server
   *  - updates the store with the uploaded document data
   * @param file The file to upload.
   * @param uploadDocumentType The type of document being uploaded.
   * @returns A promise that resolves to the uploaded document data.
   */
  async uploadDocumentImage(file: File): Promise<IHttpStatus> {
    //extract the selected document type (type required by scanovate api) from the store
    const uploadDocumentType = this.getSelectedDocumentType();
    //create the upload document model
    let model: IScanDocumentModel;
    try {
      model = await this.#createUploadDocumentModel(file, uploadDocumentType);
    } catch (error) {
      if (
        error instanceof ImageLoadError &&
        (file.type === <TImageFileType>'image/heic' || file.type === <TImageFileType>'image/heif')
      ) {
        this.#navAdapter.toAlert('document-upload-format-alert');
      }
    }

    if (!model) {
      return Promise.reject(new Error('Failed to create upload document model'));
    }

    return lastValueFrom(
      this.#httpService.post<IScanDocumentModel, IScanDocumentData>('upload-document', model).pipe(
        catchError((error) => {
          return this.#uploadDocumentSeverities.inspect(error);
        }),

        tapResult((data: IScanDocumentData) => {
          const newData: IScanDocumentData = this.createDocumentImageData(data);
          this.#scanDocumentStore.dispatchUpdateLastScanAction('upload');
          this.#scanDocumentStore.dispatchUploadDocumentSuccess(newData);
        }),
        filterStatus()
      )
    );
  }

  /**
   * @description
   *  Creates the document image data to be stored in the store after uploading a document.
   *  We want to avoid storing the cropped image in the store under a generic property and instead assign it to the correct property based on which side of the document was scanned.
   *  This way we can easily display the correct image in the UI without having to check which side was scanned every time.
   *  - updates the data object with the scan results (isBiometric, isMaxAttemptsExceeded, isBackScanned, isFrontScanned).
   * @param data The data returned from the upload document API, containing the cropped image and scan results.
   * @returns The updated document image data.
   */
  private createDocumentImageData(resp: IScanDocumentData): IScanDocumentData {
    const { croppedImage, isBackScanned, isBiometric, isFrontScanned, isMaxAttemptsExceeded } = resp;
    const data = { ...this.data$$() };
    if (isBackScanned) data.backSideDocumentImage = croppedImage;
    if (isFrontScanned) data.frontSideDocumentImage = croppedImage;
    data.isBiometric = isBiometric;
    data.isMaxAttemptsExceeded = isMaxAttemptsExceeded;
    data.isBackScanned = isBackScanned;
    data.isFrontScanned = isFrontScanned;
    return data;
  }
  /**
   *
   * @param predicate A function to test each document option.
   * @returns The document option that matches the predicate.
   */
  private getDocument(predicate: (option: IUploadDocumentType) => boolean): IUploadDocumentType | undefined {
    return this.documents$$()?.find(predicate);
  }
  /**
   *
   * @returns The description of the selected document.
   */
  getSelectedDocumentDesc(): string {
    return (
      this.getDocument((option) => option.docCode === this.#scanDocumentStore.model$$()?.selectedDocId)?.docName ?? ''
    );
  }

  /**
   *
   * @returns The description of the document that is not selected.
   */
  getNotSelectedDocumentDesc(): string {
    return (
      this.getDocument((option) => option.docCode !== this.#scanDocumentStore.model$$()?.selectedDocId)?.docName ?? ''
    );
  }

  /**
   * @description
   * extracts the selected document type required by the scanovate API from the store
   * @returns selected document type for scanovate API
   */
  getSelectedDocumentType(): string {
    return (
      this.getDocument((option) => {
        return option.docCode === this.#scanDocumentStore.model$$()?.selectedDocId;
      })?.uploadDocumentType ?? ''
    );
  }
  /**
   * @description Scans the selected document via OCR.
   * by navigating to the scan document feature with the selected document id.
   */
  scanSelectedDocumentViaOcr() {
    this.#navAdapter.toScanDocument(this.#scanDocumentStore.model$$()?.selectedDocId);
  }
  changeSelectedDocument() {
    const selectedDocId = this.getDocument(
      (option) => option.docCode !== this.#scanDocumentStore.model$$()?.selectedDocId
    )?.docCode;
    this.selectNewDocument(selectedDocId);
  }
  getDocumentsType() {
    return this.#httpService.fetch<null, IScanMethodData>(`scan-method`).pipe(
      takeOnce(),
      tap((response) => {
        const data = response.result;
        this.#scanDocumentStore.dispatchLoadScanMethodSuccess({ data });
      })
    );
  }

  deleteBackSideDocument(): Observable<IScanDocumentData> {
    return this.#httpService.post<IScanDocumentModel, IScanDocumentData>('upload/delete-back-side', null).pipe(
      tap(() => {
        this.#scanDocumentStore.dispatchDeleteBackSideDocument();
      })
    );
  }
  deleteFrontSideDocument(): Observable<IScanDocumentData> {
    return this.#httpService.post<IScanDocumentModel, IScanDocumentData>('upload/delete-front-side', null).pipe(
      tap(() => {
        this.#scanDocumentStore.dispatchDeleteFrontSideDocument();
      })
    );
  }
  updateSelectedDocument(selectedDocumentId: number) {
    this.#scanDocumentStore.dispatchLoadScanDocument(selectedDocumentId);
  }
  resetUploadedDocuments() {
    this.#scanDocumentStore.dispatchResetUploadedDocuments();
  }
  /**
   * @description
   *  Selects a new document and resets the uploaded documents.
   * @param selectedDocumentId The ID of the new document to select.
   */
  selectNewDocument(selectedDocumentId: number) {
    this.#scanDocumentStore.dispatchLoadScanDocument(selectedDocumentId);
    this.resetUploadedDocuments();
  }
}
